import { Box, Checkbox, FormControlLabel } from '@mui/material';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { bulkCloseContentRequested } from 'features/layout/features/content/actions';
import { useBulkCloseContent } from 'features/layout/grid-panel/useBulkCloseContent';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { BulkCloseContentScope } from 'features/layout/types';
import { useDontShowAgain } from 'hooks/useDontShowAgain';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const MODAL_NAME = 'bulkCloseContentConfirmation';

export const generateDontShowAgainKey = (scope: BulkCloseContentScope) => `${MODAL_NAME}_${scope}`;

const titleKey = (scope: BulkCloseContentScope) =>
  scope === 'all'
    ? 'bulk_close_content_confirmation.title'
    : `bulk_close_content_confirmation.title_${scope}`;

const descriptionKey = (scope: BulkCloseContentScope) =>
  scope === 'all'
    ? 'bulk_close_content_confirmation.description'
    : `bulk_close_content_confirmation.description_${scope}`;

type ModalData = {
  scope?: BulkCloseContentScope;
};

type ScopeConfig = {
  permissionKey:
    'canManageAllContent' | 'canManageFiles' | 'canManageScreenshares' | 'canManageWhiteboards';
};

const SCOPE_CONFIG: Record<BulkCloseContentScope, ScopeConfig> = {
  all: { permissionKey: 'canManageAllContent' },
  files: { permissionKey: 'canManageFiles' },
  screenshares: { permissionKey: 'canManageScreenshares' },
  whiteboards: { permissionKey: 'canManageWhiteboards' },
};

const BulkCloseContentConfirmationModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalData = useAppSelector(selectActiveModalData) as ModalData;

  const { scope = 'all' } = modalData;

  const permissions = useBulkCloseContent();

  const config = SCOPE_CONFIG[scope];

  const shouldClose = useMemo(
    () => !permissions[config.permissionKey],
    [permissions, config.permissionKey]
  );

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal(MODAL_NAME));
    }
  }, [shouldClose, dispatch]);

  const confirm = () => {
    dispatch(bulkCloseContentRequested(scope));
    dispatch(closeModal(MODAL_NAME));
  };

  const { dontShowAgain, handleChange, savePreference } = useDontShowAgain({
    modalName: generateDontShowAgainKey(scope),
    onConfirm: confirm,
  });

  const handleCancel = () => {
    dispatch(closeModal(MODAL_NAME));
  };

  const handleOk = () => {
    savePreference();
    confirm();
  };

  return (
    <ConfirmationDialog
      open={!shouldClose}
      title={t(titleKey(scope))}
      description={t(descriptionKey(scope))}
      cancelText={t('bulk_close_content_confirmation.btn_cancel_label')}
      okText={t('bulk_close_content_confirmation.btn_ok_label')}
      onOk={handleOk}
      onCancel={handleCancel}
      hideCloseButton
    >
      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(event) => handleChange(event.target.checked)}
            />
          }
          label={t('bulk_close_content_confirmation.dont_show_again')}
        />
      </Box>
    </ConfirmationDialog>
  );
};

export default BulkCloseContentConfirmationModal;
